import { Box, Grid, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import config from 'configs/app';
import Skeleton from 'ui/shared/chakra/Skeleton';
import type { IconName } from 'ui/shared/IconSvg';
import IconSvg from 'ui/shared/IconSvg';
import NativeTokenIcon from 'ui/shared/NativeTokenIcon';

const CHAIN_RPC_URL = config.chain.rpcUrls?.[0] || '';
const CURRENCY_SYMBOL = config.chain.currency.symbol || 'ETH';

interface StatsData {
  coinPrice: string;
  coinPriceChange: string;
  marketCap: string;
  totalBlocks: string;
  totalTransactions: string;
  tps: string;
  medGasPrice: string;
  gasPriceUsd: string;
  lastFinalizedBlock: string;
  lastSafeBlock: string;
  txHistory: Array<{ date: string; count: number }>;
  isLoading: boolean;
  error: string | null;
}

const MiniChart = ({
  data,
  isLoading,
  gridStroke,
  labelFill,
  lineStroke,
  tooltipBg,
  tooltipBorder,
  tooltipText,
}: {
  data: Array<{ date: string; count: number }>;
  isLoading: boolean;
  gridStroke: string;
  labelFill: string;
  lineStroke: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
}) => {
  const [ hoverIndex, setHoverIndex ] = React.useState<number | null>(null);

  if (isLoading || data.length === 0) {
    return <Skeleton height="80px" width="100%"/>;
  }

  const values = data.map((item) => item.count);
  const allZero = values.every(v => v === 0);
  if (allZero) {
    return (
      <Flex height="80px" width="100%" alignItems="center" justifyContent="center">
        <Text fontSize="xs" color={ labelFill }>No transaction data yet</Text>
      </Flex>
    );
  }

  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const width = 400;
  const height = 100;
  const padding = 10;

  const points = values.map((value, index) => {
    const x = padding + (index / Math.max(values.length - 1, 1)) * (width - 2 * padding);
    const y = height - padding - ((value - min) / range) * (height - 2 * padding);
    return { x, y, value, date: data[index].date };
  });

  const polyline = points.map((point) => `${ point.x },${ point.y }`).join(' ');
  const axisDates = [ data[0]?.date, data[data.length - 1]?.date ].filter(Boolean);
  const hovered = hoverIndex !== null ? points[hoverIndex] : null;

  const formatAxisDate = (dateStr: string) => {
    const date = new Date(`${ dateStr }T00:00:00Z`);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  const formatTooltipDate = (dateStr: string) => {
    const date = new Date(`${ dateStr }T00:00:00Z`);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  return (
    <Box
      position="relative"
      onMouseLeave={ () => setHoverIndex(null) }
    >
      <svg width="100%" height={ height + 20 } viewBox={ `0 0 ${ width } ${ height + 20 }` } preserveAspectRatio="none">
        <line x1={ padding } y1={ padding } x2={ padding } y2={ height - padding } stroke={ gridStroke } strokeWidth="1"/>
        <line x1={ padding } y1={ height - padding } x2={ width - padding } y2={ height - padding } stroke={ gridStroke } strokeWidth="1"/>
        <polyline fill="none" stroke={ lineStroke } strokeWidth="2" points={ polyline }/>
        <text x={ padding - 5 } y={ padding + 5 } fontSize="10" fill={ labelFill } textAnchor="end">{ max >= 1000 ? `${ (max / 1000).toFixed(0) }k` : String(max) }</text>
        <text x={ padding - 5 } y={ height - padding } fontSize="10" fill={ labelFill } textAnchor="end">{ min >= 1000 ? `${ (min / 1000).toFixed(0) }k` : String(min) }</text>
        { axisDates.map((date, i) => (
          <text
            key={ `${ date }-${ i }` }
            x={ padding + (i / Math.max(axisDates.length - 1, 1)) * (width - 2 * padding) }
            y={ height + 12 }
            fontSize="10"
            fill={ labelFill }
            textAnchor="middle"
          >
            { formatAxisDate(date) }
          </text>
        )) }
        { points.map((point, index) => (
          <circle
            key={ point.date }
            cx={ point.x }
            cy={ point.y }
            r={ hoverIndex === index ? 4.5 : 3 }
            fill={ hoverIndex === index ? lineStroke : 'transparent' }
            stroke={ lineStroke }
            strokeWidth={ hoverIndex === index ? 2 : 0 }
            style={{ cursor: 'pointer' }}
            onMouseEnter={ () => setHoverIndex(index) }
          />
        )) }
        { /* Invisible hit targets for easier hover */ }
        { points.map((point, index) => (
          <rect
            key={ `hit-${ point.date }` }
            x={ point.x - ((width - 2 * padding) / Math.max(points.length, 1)) / 2 }
            y={ padding }
            width={ (width - 2 * padding) / Math.max(points.length, 1) }
            height={ height - 2 * padding }
            fill="transparent"
            onMouseEnter={ () => setHoverIndex(index) }
          />
        )) }
        { hovered && (
          <line
            x1={ hovered.x }
            y1={ padding }
            x2={ hovered.x }
            y2={ height - padding }
            stroke={ gridStroke }
            strokeWidth="1"
            strokeDasharray="3 3"
          />
        ) }
      </svg>
      { hovered && (
        <Box
          position="absolute"
          top="8px"
          left={ `${ Math.min(Math.max((hovered.x / width) * 100, 18), 82) }%` }
          transform="translateX(-50%)"
          bg={ tooltipBg }
          border="1px solid"
          borderColor={ tooltipBorder }
          borderRadius="6px"
          px={ 2.5 }
          py={ 1.5 }
          boxShadow="sm"
          pointerEvents="none"
          zIndex={ 2 }
          minW="120px"
          textAlign="center"
        >
          <Text fontSize="11px" color={ labelFill }>{ formatTooltipDate(hovered.date) }</Text>
          <Text fontSize="13px" fontWeight="600" color={ tooltipText }>
            { hovered.value.toLocaleString() } txns
          </Text>
        </Box>
      ) }
    </Box>
  );
};

async function fetchTxHistory14d(): Promise<Array<{ date: string; count: number }>> {
  try {
    const res = await fetch('/node-api/proxy/api/v2/stats/charts/transactions', {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      return [];
    }
    const json = await res.json() as {
      chart_data?: Array<{ date: string; transactions_count?: number; transaction_count?: number | null }>;
    };
    const rows = (json.chart_data || [])
      .map((item) => ({
        date: item.date,
        count: Number(item.transactions_count ?? item.transaction_count ?? 0) || 0,
      }))
      .filter((item) => Boolean(item.date))
      .sort((a, b) => a.date.localeCompare(b.date));

    return rows.slice(-14);
  } catch {
    return [];
  }
}

async function rpcCall(method: string, params: unknown[] = []): Promise<unknown> {
  if (!CHAIN_RPC_URL) return null;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(CHAIN_RPC_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jsonrpc: '2.0', method, params, id: 1 }),
      signal: controller.signal,
    });
    const json = await res.json();
    return json?.result ?? null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

async function fetchBlockscoutStats(): Promise<Record<string, unknown> | null> {
  try {
    const res = await fetch('/api/stats', { method: 'GET', headers: { Accept: 'application/json' } });
    if (res.ok) {
      return await res.json();
    }
  } catch { /* ignore */ }
  return null;
}

const Stats = () => {
  const [ statsData, setStatsData ] = React.useState<StatsData>({
    coinPrice: '',
    coinPriceChange: '',
    marketCap: '',
    totalBlocks: '0',
    totalTransactions: '0',
    tps: '0.0',
    medGasPrice: '0',
    gasPriceUsd: '',
    lastFinalizedBlock: '0',
    lastSafeBlock: '0',
    txHistory: [],
    isLoading: true,
    error: null,
  });

  React.useEffect(() => {
    const fetchData = async() => {
      try {
        const bsStats = await fetchBlockscoutStats();

        let coinPrice = '';
        let coinPriceChange = '';
        let marketCap = '';
        let totalTxsNum = 0;
        let totalBlocks = 0;
        let avgBlockTime = 0;

        if (bsStats) {
          if (bsStats.coin_price && Number(bsStats.coin_price) > 0) {
            coinPrice = `$${ Number(bsStats.coin_price).toFixed(4) }`;
          }
          if (bsStats.coin_price_change_percentage != null) {
            const pct = Number(bsStats.coin_price_change_percentage);
            if (!isNaN(pct) && pct !== 0) {
              coinPriceChange = `${ pct > 0 ? '+' : '' }${ pct.toFixed(2) }%`;
            }
          }
          if (bsStats.market_cap && Number(bsStats.market_cap) > 0) {
            marketCap = `$${ Number(bsStats.market_cap).toLocaleString() }`;
          }
          if (bsStats.total_transactions) {
            totalTxsNum = Number(bsStats.total_transactions);
          }
          if (bsStats.total_blocks) {
            totalBlocks = Number(bsStats.total_blocks);
          }
          if (bsStats.average_block_time) {
            avgBlockTime = Number(bsStats.average_block_time) / 1000;
          }
        }

        let latestBlock = totalBlocks;
        let gasPriceGwei = '0';
        let gasPriceUsd = '';
        let finalizedBlock = 0;
        let safeBlock = 0;

        if (CHAIN_RPC_URL) {
          const [blockNumResult, gasPriceResult, finalizedResult, safeResult] = await Promise.allSettled([
            rpcCall('eth_blockNumber'),
            rpcCall('eth_gasPrice'),
            rpcCall('eth_getBlockByNumber', [ 'finalized', false ]),
            rpcCall('eth_getBlockByNumber', [ 'safe', false ]),
          ]);

          if (blockNumResult.status === 'fulfilled' && blockNumResult.value) {
            latestBlock = parseInt(blockNumResult.value as string, 16);
          }

          if (gasPriceResult.status === 'fulfilled' && gasPriceResult.value) {
            const gasPriceWei = parseInt(gasPriceResult.value as string, 16);
            gasPriceGwei = (gasPriceWei / 1e9).toFixed(3);
            if (coinPrice) {
              const priceNum = parseFloat(coinPrice.replace('$', ''));
              gasPriceUsd = `$${ (gasPriceWei / 1e18 * priceNum * 21000).toFixed(4) }`;
            }
          }

          if (finalizedResult.status === 'fulfilled' && finalizedResult.value) {
            const block = finalizedResult.value as Record<string, string>;
            finalizedBlock = block?.number ? parseInt(block.number, 16) : latestBlock;
          } else {
            finalizedBlock = latestBlock;
          }

          if (safeResult.status === 'fulfilled' && safeResult.value) {
            const block = safeResult.value as Record<string, string>;
            safeBlock = block?.number ? parseInt(block.number, 16) : latestBlock;
          } else {
            safeBlock = latestBlock;
          }
        }

        const tps = totalTxsNum > 0 && avgBlockTime > 0
          ? (totalTxsNum / (totalBlocks * avgBlockTime)).toFixed(1)
          : '0.0';

        const txHistory = await fetchTxHistory14d();

        setStatsData({
          coinPrice,
          coinPriceChange,
          marketCap,
          totalBlocks: totalBlocks.toLocaleString(),
          totalTransactions: totalTxsNum.toLocaleString(),
          tps,
          medGasPrice: gasPriceGwei,
          gasPriceUsd,
          lastFinalizedBlock: finalizedBlock.toLocaleString(),
          lastSafeBlock: safeBlock.toLocaleString(),
          txHistory,
          isLoading: false,
          error: null,
        });
      } catch {
        setStatsData(prev => ({
          ...prev,
          isLoading: false,
          error: 'Failed to fetch data',
        }));
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 12000);
    return () => clearInterval(interval);
  }, []);

  const statItemBg = useColorModeValue('white', '#16161B');
  const gridBg = useColorModeValue('#e9ecef', 'gray.600');
  const chartColBg = useColorModeValue('white', '#16161B');
  const chartGridStroke = useColorModeValue('#e9ecef', '#4a5568');
  const chartLabelFill = useColorModeValue('#6c757d', '#a0aec0');
  const chartLineStroke = useColorModeValue('#000', '#e2e8f0');
  const chartTooltipBg = useColorModeValue('white', '#1a202c');
  const chartTooltipBorder = useColorModeValue('#dee2e6', '#4a5568');
  const chartTooltipText = useColorModeValue('#212529', '#f7fafc');
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const valueColor = useColorModeValue('gray.800', 'gray.200');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const StatItem = ({
    icon,
    iconComponent,
    label,
    value,
    subValue,
    subValueColor,
    isLoading: itemLoading,
    small,
  }: {
    icon?: IconName;
    iconComponent?: React.ReactNode;
    label: string;
    value: string;
    subValue?: string;
    subValueColor?: string;
    isLoading?: boolean;
    small?: boolean;
  }) => (
    <Box bg={ statItemBg } p={ small ? 3 : 5 } h="100%">
      <Flex alignItems="flex-start" gap={ 3 }>
        { (icon || iconComponent) && (
          <Box
            borderRadius="lg"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            { iconComponent ?? (icon && <IconSvg name={ icon } boxSize={ 6 } color={ valueColor }/>) }
          </Box>
        ) }
        <Box>
          <Text fontSize="xs" color={ labelColor } textTransform="uppercase" fontWeight="medium">
            { label }
          </Text>
          { itemLoading ? (
            <Skeleton height="24px" width="100px" mt={ 1 }/>
          ) : (
            <Text fontSize={ small ? 'md' : '15px' } fontWeight="normal" color={ valueColor }>
              { value || '-' }
              { subValue && (() => {
                let color = subValueColor;
                if (!color) {
                  if (subValue.startsWith('+')) color = 'green.500';
                  else if (subValue.startsWith('-')) color = 'red.500';
                  else color = 'gray.500';
                }
                return (
                  <Text as="span" fontSize="xs" color={ color } ml={ 1 }>
                    { subValue }
                  </Text>
                );
              })() }
            </Text>
          ) }
        </Box>
      </Flex>
    </Box>
  );

  return (
    <Grid
      gridTemplateColumns={{ base: '1fr', lg: '1fr 1fr 1fr' }}
      gap="1px"
      bg={ gridBg }
      flexGrow={ 1 }
    >
      { /* Column 1 - Price/Blocks & Market Cap */ }
      <Box>
        <Grid templateRows="1fr 1fr" gap="1px" h="100%">
          { statsData.coinPrice ? (
            <StatItem
              iconComponent={ <NativeTokenIcon boxSize={ 6 }/> }
              label={ `${ CURRENCY_SYMBOL } PRICE` }
              value={ statsData.coinPrice }
              subValue={ statsData.coinPriceChange }
            />
          ) : (
            <StatItem
              icon="block_slim"
              label="TOTAL BLOCKS"
              value={ statsData.totalBlocks }
              isLoading={ statsData.isLoading }
            />
          ) }
          { statsData.marketCap ? (
            <StatItem
              icon="globe"
              label="MARKET CAP"
              value={ statsData.marketCap }
            />
          ) : (
            <StatItem
              icon="clock"
              label="AVG BLOCK TIME"
              value="3.0s"
              isLoading={ statsData.isLoading }
            />
          ) }
        </Grid>
      </Box>

      { /* Column 2 - Transactions & Gas / Blocks */ }
      <Box>
        <Grid templateRows="1fr 1fr" h="100%" backgroundColor="card_bg">
          <Grid
            templateColumns="1fr 1fr"
            borderBottom="1px solid"
            borderColor={ borderColor }
            padding="5px"
          >
            <StatItem
              icon="transactions_slim"
              label="TRANSACTIONS"
              value={ statsData.totalTransactions }
              subValue={ `(${ statsData.tps } TPS)` }
              subValueColor="gray.500"
              isLoading={ statsData.isLoading }
              small
            />
            <StatItem
              icon="gas"
              label="MED GAS PRICE"
              value={ `${ statsData.medGasPrice } Gwei` }
              subValue={ statsData.gasPriceUsd ? `(${ statsData.gasPriceUsd })` : undefined }
              subValueColor="gray.500"
              isLoading={ statsData.isLoading }
              small
            />
          </Grid>

          <Grid templateColumns="1fr 1fr" padding="5px">
            <StatItem
              icon="block_slim"
              label="LAST FINALIZED BLOCK"
              value={ statsData.lastFinalizedBlock }
              isLoading={ statsData.isLoading }
              small
            />
            <StatItem
              icon="block_slim"
              label="LAST SAFE BLOCK"
              value={ statsData.lastSafeBlock }
              isLoading={ statsData.isLoading }
              small
            />
          </Grid>
        </Grid>
      </Box>

      { /* Column 3 - Transaction History Chart */ }
      <Box bg={ chartColBg } p={ 4 }>
        <Text fontSize="xs" color={ labelColor } textTransform="uppercase" fontWeight="medium" mb={ 2 } >
          TRANSACTION HISTORY IN 14 DAYS
        </Text>
        <MiniChart
          data={ statsData.txHistory }
          isLoading={ statsData.isLoading }
          gridStroke={ chartGridStroke }
          labelFill={ chartLabelFill }
          lineStroke={ chartLineStroke }
          tooltipBg={ chartTooltipBg }
          tooltipBorder={ chartTooltipBorder }
          tooltipText={ chartTooltipText }
        />
      </Box>
    </Grid>
  );
};

export default Stats;
